import type {
  ActionType,
  ActionsResponse,
  CreateActionPayload,
  CreateActionResponse,
  DeleteActionResponse,
  EndOfActionResponse,
  RiskType,
  UpdateActionPayload,
  UpdateActionResponse,
} from '@/domains/academicRisk/academicRiskAction.entity';
import { ApiEndPoint } from '@/domains/api/api.entity';
import { apiRequest } from './apiRequest';

/**
 * Servicio API para gestión de acciones académicas
 * Responsable de las comunicaciones HTTP relacionadas con el CRUD de acciones académicas
 * y obtención de catálogos (tipos de acciones y riesgos)
 */
class AcademicRiskActionApiService {
  /**
   * Obtiene todas las acciones académicas registradas para un estudiante específico
   * @param cod_incorporacion_estudiante - Código único de incorporación del estudiante
   */
  async getActions(
    cod_incorporacion_estudiante: string,
  ): Promise<ActionsResponse> {
    const actionResponse = await apiRequest.get<ActionsResponse>({
      route: ApiEndPoint.STUDENT_ACTIONS,
      param: `/${cod_incorporacion_estudiante}/acciones`,
    });

    return actionResponse;
  }

  /**
   * Obtiene el catálogo completo de tipos de acciones disponibles en el sistema
   */
  async getActionTypes(): Promise<ActionType[]> {
    const response = await apiRequest.get<ActionType[]>({
      route: ApiEndPoint.ACTION_TYPES,
    });

    return response;
  }

  /**
   * Obtiene el catálogo completo de tipos de riesgos académicos definidos
   */
  async getRiskTypes(): Promise<RiskType[]> {
    const response = await apiRequest.get<RiskType[]>({
      route: ApiEndPoint.RISK,
    });

    return response;
  }

  /**
   * Registra una nueva acción académica para uno o varios estudiantes
   * @param payload - Datos de la acción incluyendo descripción, fecha, tipo, funcionario y estudiantes
   */
  async createAction(
    payload: CreateActionPayload,
  ): Promise<CreateActionResponse> {
    const createActionResponse = await apiRequest.post<
      CreateActionResponse,
      CreateActionPayload
    >({
      route: ApiEndPoint.ACTIONS_RECORD,
      payload,
    });

    return createActionResponse;
  }

  /**
   * Actualiza las observaciones y acuerdos de una incorporación de acción existente
   * @param cod_incorporacion_accion - Código único de la incorporación de acción a actualizar
   * @param payload - Datos actualizados (descripción de incorporación y acuerdo)
   */
  async updateAction(
    cod_incorporacion_accion: number,
    payload: UpdateActionPayload,
  ): Promise<UpdateActionResponse> {
    const updateActionResponse = await apiRequest.put<
      UpdateActionResponse,
      UpdateActionPayload
    >({
      route: ApiEndPoint.ACTIONS_UPDATE,
      param: `/${cod_incorporacion_accion}`,
      payload,
    });

    return updateActionResponse;
  }

  async endOfAction(
    cod_incorporacion_accion: number,
  ): Promise<EndOfActionResponse> {
    const response = await apiRequest.put<EndOfActionResponse, number>({
      route: ApiEndPoint.END_OF_ACTION,
      param: `/${cod_incorporacion_accion}`,
    });

    return response;
  }

  /**
   * Elimina una incorporación de acción académica del sistema
   * Realiza eliminación en cascada de la acción y su incorporación
   * @param cod_incorporacion_accion - Código único de la incorporación de acción a eliminar
   */
  async deleteAction(
    cod_incorporacion_accion: number,
  ): Promise<DeleteActionResponse> {
    const deleteActionResponse = await apiRequest.delete<DeleteActionResponse>({
      route: ApiEndPoint.ACTIONS_DELETE,
      param: `/${cod_incorporacion_accion}`,
    });

    return deleteActionResponse;
  }
}

export const academicRiskActionApiService = new AcademicRiskActionApiService();
